/*
Purpose: Modern two-tab results panel for V4 analysis results
Features: Summary tab with stats, Report tab with details button, professional privacy toggle
*/
import React, { useState } from 'react';
import { FaFileAlt, FaClipboardList, FaSearchPlus, FaLock, FaGlobe } from 'react-icons/fa';
import '../../styles/modern-results-panel.css';
import { useJobAccess } from '../../../../hooks/useJobAccess';
import LockedJobView from '../../../jobs/LockedJobView';

const ModernResultsPanel = ({
  job,
  analysisData,
  currentUser,
  onPrivacyToggle,
  onOpenDetails
}) => {
  const [activeTab, setActiveTab] = useState('summary');

  // Use access control hook
  const { loading: accessLoading, hasAccess, isOwner } = useJobAccess(currentUser, job);
  
  // Legacy fallback
  const isPublic = job?.isPublic || false;

  // Extract V4 data
  const targets = analysisData?.targets || [];
  const targetsByType = {
    vehicles: targets.filter(t => t.type === 'vehicle'),
    environment: targets.filter(t => t.type === 'environment'),
    videographer: targets.filter(t => t.type === 'videographer'),
    people: targets.filter(t => t.type === 'person'),
    other: targets.filter(t => !['vehicle', 'environment', 'videographer', 'person'].includes(t.type))
  };

  // Calculate stats
  const totalActivities = targets.reduce((sum, t) => sum + (t.actions?.activities?.length || 0), 0);
  const avgConfidence = targets.length > 0 
    ? Math.round((targets.reduce((sum, t) => sum + (t.confidence || 0), 0) / targets.length) * 100)
    : 0;

  // Get backend-generated summary (single source of truth)
  const backendSummary = job?.results?.summary || job?.analysisText;
  const aiNarrative = analysisData?.forensic_summary || backendSummary || 'Analysis completed.';
  
  // Helper functions (defined BEFORE use to avoid hoisting issues)
  
  // Helper: Generate temporal narrative
  const generateTemporalNarrative = (targets, timeline) => {
    if (timeline.length === 0) return null;
    
    const parts = [];
    timeline.slice(0, 5).forEach(event => {
      const timestamp = event.timestamp_display || event.timestamp || '00:00:00';
      const desc = event.description || event.event_type || 'Event';
      const capitalizedDesc = desc.charAt(0).toUpperCase() + desc.slice(1);
      parts.push(`${timestamp} - ${capitalizedDesc}.`);
    });
    
    if (timeline.length > 5) {
      parts.push(`(${timeline.length - 5} more events)`);
    }
    
    return parts.join(' ');
  };
  
  // Helper: Generate legal narrative
  const generateLegalNarrative = (targets, timeline, data) => {
    const vehicles = targets.filter(t => t.type === 'vehicle');
    const damagedVehicles = vehicles.filter(v => {
      const damage = v.attributes?.damage;
      return damage && damage !== 'none' && damage !== 'unknown';
    });
    
    if (damagedVehicles.length === 0) return null;
    
    const parts = [];
    damagedVehicles.forEach(v => {
      const attrs = v.attributes || {};
      const color = attrs.color || '';
      const make = attrs.make || '';
      const model = attrs.model || '';
      const vehicleDesc = `${color} ${make} ${model}`.trim().toUpperCase();
      const damage = attrs.damage;
      const severity = attrs.damage_severity || 'visible';
      
      parts.push(`SUBJECT VEHICLE: ${vehicleDesc} - ${severity.toUpperCase()} DAMAGE: ${damage}.`);
      
      const plate = attrs.license_plate;
      if (plate && plate !== 'not visible' && plate !== 'unknown') {
        parts.push(`License plate: "${plate}".`);
      }
    });
    
    return parts.join(' ');
  };
  
  // Helper: Generate brief summary
  const generateBriefSummary = (targets) => {
    const counts = {
      vehicles: targets.filter(t => t.type === 'vehicle').length,
      people: targets.filter(t => t.type === 'person').length
    };
    
    const parts = [];
    if (counts.vehicles > 0) parts.push(`${counts.vehicles} vehicle${counts.vehicles > 1 ? 's' : ''}`);
    if (counts.people > 0) parts.push(`${counts.people} person${counts.people > 1 ? 's' : ''}`);
    
    return parts.length > 0 ? `Detected: ${parts.join(', ')}` : null;
  };
  
  // Generate all narrative variations
  const generateClientNarratives = () => {
    if (!analysisData || !analysisData.targets || analysisData.targets.length === 0) {
      return null;
    }
    
    const targets = analysisData.targets || [];
    const timeline = analysisData.master_timeline || [];
    
    return {
      temporalNarrative: generateTemporalNarrative(targets, timeline),
      legalNarrative: generateLegalNarrative(targets, timeline, analysisData),
      briefSummary: generateBriefSummary(targets)
    };
  };
  
  const narratives = generateClientNarratives();

  // Show loading state while checking access
  if (accessLoading) {
    return (
      <div className="modern-results-panel">
        <div className="access-checking">
          ⏳ Checking access...
        </div>
      </div>
    );
  }

  // Show locked view if no access
  if (!hasAccess) {
    return <LockedJobView job={job} user={currentUser} />;
  }

  return (
    <div className="modern-results-panel">
      {/* Header with Privacy Toggle */}
      <div className="mrp-header">
        <div className="mrp-title-section">
          <h3 className="mrp-title">ANALYSIS RESULTS</h3>
          {!isOwner && !hasAccess && (
            <span className="mrp-badge-premium">
              <FaLock /> PREMIUM
            </span>
          )}
        </div>

        {/* Privacy Toggle for owners */}
        {isOwner && onPrivacyToggle && (
          <div className="mrp-privacy-toggle">
            <span className="mrp-privacy-label">Visibility:</span>
            <div className="privacy-toggle-switch">
              <input
                type="checkbox"
                id={`privacy-toggle-${job.id}`}
                className="privacy-toggle-input"
                checked={isPublic}
                onChange={(e) => {
                  e.stopPropagation();
                  onPrivacyToggle(job.id, e.target.checked);
                }}
              />
              <label htmlFor={`privacy-toggle-${job.id}`} className="privacy-toggle-label">
                <span className="privacy-toggle-inner">
                  <span className="privacy-toggle-switch-left">
                    <FaLock /> Private
                  </span>
                  <span className="privacy-toggle-switch-right">
                    <FaGlobe /> Public
                  </span>
                </span>
                <span className="privacy-toggle-slider"></span>
              </label>
            </div>
          </div>
        )}

        {/* Confidence Badge */}
        {avgConfidence > 0 && (
          <div className={`mrp-confidence-badge ${avgConfidence >= 90 ? 'high' : avgConfidence >= 70 ? 'medium' : 'low'}`}>
            <span className="mrp-confidence-dot"></span>
            {avgConfidence}% Confidence
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mrp-tabs">
        <button
          className={`mrp-tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          <FaFileAlt />
          <span>Summary</span>
        </button>
        <button
          className={`mrp-tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          <FaClipboardList />
          <span>Notes</span>
        </button>
        <button
          className={`mrp-tab ${activeTab === 'fullreport' ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails();
          }}
        >
          <FaSearchPlus />
          <span>Report</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="mrp-content">
        {activeTab === 'summary' ? (
          <div className="mrp-summary-tab">
            {/* Primary Narrative (AI's forensic summary) */}
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <h4 style={{ 
                fontSize: 'var(--text-xs)', 
                fontWeight: 'var(--font-bold)', 
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 'var(--space-2)'
              }}>
                📋 FORENSIC NARRATIVE
              </h4>
              <div className="mrp-summary-text" style={{ marginBottom: 0 }}>
                {aiNarrative}
              </div>
            </div>

            {/* Brief Summary */}
            {narratives?.briefSummary && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <h4 style={{ 
                  fontSize: 'var(--text-xs)', 
                  fontWeight: 'var(--font-semibold)', 
                  color: '#3B82F6',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 'var(--space-2)'
                }}>
                  ⚡ QUICK SUMMARY
                </h4>
                <div style={{ padding: 'var(--space-2)', background: 'rgba(59, 130, 246, 0.08)', borderRadius: '6px', border: '1px solid rgba(59, 130, 246, 0.2)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                  {narratives.briefSummary}
                </div>
              </div>
            )}

            {/* Temporal Narrative */}
            {narratives?.temporalNarrative && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <h4 style={{ 
                  fontSize: 'var(--text-xs)', 
                  fontWeight: 'var(--font-semibold)', 
                  color: '#10B981',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 'var(--space-2)'
                }}>
                  🕐 TIMELINE NARRATIVE
                </h4>
                <div style={{ padding: 'var(--space-2)', background: 'rgba(16, 185, 129, 0.08)', borderRadius: '6px', border: '1px solid rgba(16, 185, 129, 0.2)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)', lineHeight: '1.6' }}>
                  {narratives.temporalNarrative}
                </div>
              </div>
            )}

            {/* Legal Narrative (if damage present) */}
            {narratives?.legalNarrative && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <h4 style={{ 
                  fontSize: 'var(--text-xs)', 
                  fontWeight: 'var(--font-semibold)', 
                  color: '#EF4444',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 'var(--space-2)'
                }}>
                  ⚖️ LEGAL FORMAT (Damage Emphasis)
                </h4>
                <div style={{ padding: 'var(--space-2)', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.2)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)', lineHeight: '1.6' }}>
                  {narratives.legalNarrative}
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'notes' ? (
          <div className="mrp-report-tab">
            {/* Quick Stats Overview */}
            <div className="mrp-report-stats">
              <div className="mrp-quick-stat">
                <span className="mrp-quick-stat-icon" style={{ color: '#3B82F6' }}>◆</span>
                <span className="mrp-quick-stat-value">{targetsByType.vehicles.length}</span>
                <span className="mrp-quick-stat-label">Vehicles</span>
              </div>
              <div className="mrp-stat-separator">|</div>
              <div className="mrp-quick-stat">
                <span className="mrp-quick-stat-icon" style={{ color: '#10B981' }}>▲</span>
                <span className="mrp-quick-stat-value">{targetsByType.environment.length}</span>
                <span className="mrp-quick-stat-label">Environment</span>
              </div>
              <div className="mrp-stat-separator">|</div>
              <div className="mrp-quick-stat">
                <span className="mrp-quick-stat-icon" style={{ color: '#F59E0B' }}>■</span>
                <span className="mrp-quick-stat-value">{targetsByType.videographer.length}</span>
                <span className="mrp-quick-stat-label">Camera</span>
              </div>
              <div className="mrp-stat-separator">|</div>
              <div className="mrp-quick-stat">
                <span className="mrp-quick-stat-icon" style={{ color: '#8B5CF6' }}>▶</span>
                <span className="mrp-quick-stat-value">{totalActivities}</span>
                <span className="mrp-quick-stat-label">Activities</span>
              </div>
            </div>

            {/* Target Summary */}
            <div className="mrp-report-section">
              <h4 className="mrp-section-title">▸ DETECTED TARGETS</h4>
              <div className="mrp-target-list">
                {targetsByType.vehicles.map((vehicle, idx) => (
                  <div key={idx} className="mrp-target-item">
                    <span className="mrp-target-icon vehicle">◆</span>
                    <span className="mrp-target-text">
                      {vehicle.attributes?.color} {vehicle.attributes?.year} {vehicle.attributes?.make} {vehicle.attributes?.model}
                    </span>
                    <span className="mrp-target-confidence">
                      {Math.round((vehicle.confidence || 0) * 100)}%
                    </span>
                  </div>
                ))}

                {targetsByType.environment.map((env, idx) => (
                  <div key={idx} className="mrp-target-item">
                    <span className="mrp-target-icon environment">▲</span>
                    <span className="mrp-target-text">
                      {env.attributes?.location_type || 'Environment'}
                    </span>
                    <span className="mrp-target-confidence">
                      {Math.round((env.confidence || 0) * 100)}%
                    </span>
                  </div>
                ))}

                {targetsByType.videographer.map((vid, idx) => (
                  <div key={idx} className="mrp-target-item">
                    <span className="mrp-target-icon videographer">■</span>
                    <span className="mrp-target-text">
                      {vid.attributes?.device_type || 'Camera'}
                    </span>
                    <span className="mrp-target-confidence">
                      {Math.round((vid.confidence || 0) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(ModernResultsPanel);

